import React, { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { API_BASE_URL } from '../config';
import {
    resolveOrderSuccessPayload,
    saveOrderSuccessSnapshot,
    formatOrderDisplayId,
    clearOrderSuccessSnapshot,
    clearCheckoutDraftStorage,
    markCheckoutCompleted,
    clearCheckoutCompleted,
} from '../utils/orderSuccessSnapshot';
import './OrderSuccess.scss';

function formatCurrency(amount) {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('en-IN');
}

function getItemTitle(item, index) {
    return (
        item.product_data?.title ||
        item.product_title ||
        item.title ||
        `Item ${index + 1}`
    );
}

function getItemImage(item) {
    const urls = item.product_data?.urls || item.urls;
    const raw = Array.isArray(urls) && urls.length ? (urls[1] || urls[0]) : (typeof urls === 'string' ? urls : null);
    if (!raw) return null;
    return String(raw).startsWith('http') ? raw : `${API_BASE_URL}/${String(raw).replace(/^\//, '')}`;
}

function formatPlacedDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (_e) {
        return '';
    }
}

export function OrderSuccess() {
    const navigate = useNavigate();
    const location = useLocation();
    const mob = isMobile;

    const order = useMemo(
        () => resolveOrderSuccessPayload(location.state),
        [location.state]
    );
    const navigationType = useNavigationType();
    const allowPopRedirectRef = useRef(false);

    useEffect(() => {
        if (!order) return;
        saveOrderSuccessSnapshot(order);
        clearCheckoutDraftStorage();
        markCheckoutCompleted();
        const t = window.setTimeout(() => {
            allowPopRedirectRef.current = true;
        }, 0);
        return () => window.clearTimeout(t);
    }, [order]);

    useEffect(() => {
        if (!order) {
            const retry = resolveOrderSuccessPayload(location.state);
            if (!retry) {
                navigate('/', { replace: true });
            }
            return;
        }
    }, [order, location.state, navigate]);

    // Browser/hardware back: leave checkout → home (not order-summary / cart)
    useEffect(() => {
        if (!order || !allowPopRedirectRef.current) return;
        if (navigationType !== 'POP') return;
        navigate('/', { replace: true });
    }, [navigationType, order, navigate]);

    useEffect(() => {
        if (!order) return;
        const goHome = () => navigate('/', { replace: true });
        window.history.pushState({ orderSuccess: true }, '', window.location.href);
        window.addEventListener('popstate', goHome);
        return () => window.removeEventListener('popstate', goHome);
    }, [order, navigate]);

    if (!order) {
        return null;
    }

    const displayId = formatOrderDisplayId(order);
    const summary = order.order_summary || {};
    const items = order.order_items || [];
    const itemCount = summary.item_count ?? items.reduce((n, i) => n + (i.selectedQty || 1), 0);
    const finalAmount = summary.final_amount ?? 0;
    const address = order.deliveryAddress;
    const placedLabel = formatPlacedDate(order.placedAt || order.createdAt);
    const previewItems = items.slice(0, 3);
    const moreCount = Math.max(0, items.length - previewItems.length);

    const breadcrumbs = [
        { label: 'Home', path: '/' },
        { label: 'My Orders', path: '/orders' },
        { label: 'Order confirmed', path: null },
    ];

    const handleContinueShopping = () => {
        clearOrderSuccessSnapshot();
        clearCheckoutCompleted();
        navigate('/', { replace: true });
    };

    const handleViewOrders = () => {
        clearOrderSuccessSnapshot();
        clearCheckoutCompleted();
        navigate('/orders', { replace: true });
    };

    return (
        <div className={`order-success-page${mob ? ' order-success-page--mob' : ''}`}>
            {mob ? (
                <header className="order-success-mob-topbar">
                    <div className="order-success-mob-topbar__icon" aria-hidden>
                        <i className="bi bi-check-circle-fill" />
                    </div>
                    <h1 className="order-success-mob-topbar__title">Order confirmed</h1>
                </header>
            ) : (
                <div className="order-success-breadcrumbs">
                    <div className="breadcrumbs-container breadcrumbs-design-6">
                        {breadcrumbs.map((crumb, index) => (
                            <React.Fragment key={crumb.label}>
                                {index > 0 && <span className="breadcrumbs-separator">/</span>}
                                {crumb.path ? (
                                    <Link to={crumb.path} className="breadcrumbs-link">
                                        {crumb.label}
                                    </Link>
                                ) : (
                                    <span className="breadcrumbs-current">{crumb.label}</span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            <div className="order-success-layout">
                <section
                    className={`order-success-hero${mob ? ' order-success-hero--mob' : ''}`}
                    aria-labelledby="order-success-title"
                >
                    <div className="order-success-hero__icon-wrap" aria-hidden>
                        <div className="order-success-hero__icon-ring" />
                        <div className="order-success-hero__icon">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M20 6L9 17l-5-5"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                    <h2 id="order-success-title" className="order-success-hero__title">
                        {mob ? 'Thank you for your order!' : 'Order placed successfully!'}
                    </h2>
                    <p className="order-success-hero__subtitle">
                        {mob
                            ? 'Your order has been received and is being processed.'
                            : (
                                <>
                                    Thank you for your purchase. We&apos;ve received your order
                                    {order.isGuest ? '' : ' and will send updates to your registered contact'}.
                                </>
                            )}
                    </p>
                    {displayId && (
                        <div className="order-success-hero__order-id">
                            <span className="order-success-hero__order-id-label">Order ID</span>
                            <span className="order-success-hero__order-id-value">#{displayId}</span>
                        </div>
                    )}
                    {placedLabel && (
                        <p className="order-success-hero__placed-at">{placedLabel}</p>
                    )}
                    {mob && (
                        <div className="order-success-mob-status-pill" role="status">
                            <i className="bi bi-shield-check" aria-hidden />
                            Payment confirmed
                        </div>
                    )}
                </section>

                <div className="order-success-panels">
                    <section className="order-success-card order-success-card--summary">
                        <h2 className="order-success-card__heading">Order summary</h2>
                        <div className="order-success-stats">
                            <div className="order-success-stat">
                                <span className="order-success-stat__label">Items</span>
                                <span className="order-success-stat__value">{itemCount}</span>
                            </div>
                            <div className="order-success-stat order-success-stat--highlight">
                                <span className="order-success-stat__label">Amount paid</span>
                                <span className="order-success-stat__value">₹{formatCurrency(finalAmount)}</span>
                            </div>
                            <div className="order-success-stat">
                                <span className="order-success-stat__label">Status</span>
                                <span className="order-success-stat__value order-success-stat__value--status">
                                    Confirmed
                                </span>
                            </div>
                        </div>
                        {(summary.total_discount > 0 || summary.coupon_discount > 0) && (
                            <div className="order-success-savings">
                                {summary.total_discount > 0 && (
                                    <span>You saved ₹{formatCurrency(summary.total_discount)} on MRP</span>
                                )}
                                {summary.coupon_discount > 0 && (
                                    <span>Coupon applied: −₹{formatCurrency(summary.coupon_discount)}</span>
                                )}
                            </div>
                        )}
                    </section>

                    {items.length > 0 && (
                        <section className="order-success-card order-success-card--items">
                            <h2 className="order-success-card__heading">
                                {items.length === 1 ? 'Your item' : 'Your items'}
                            </h2>
                            <ul className="order-success-item-list">
                                {previewItems.map((item, index) => {
                                    const img = getItemImage(item);
                                    const title = getItemTitle(item, index);
                                    return (
                                        <li key={`${item.product_id}-${item.selectedSize}-${index}`} className="order-success-item">
                                            <div className="order-success-item__thumb">
                                                {img ? (
                                                    <img src={img} alt="" />
                                                ) : (
                                                    <span className="order-success-item__thumb-placeholder" aria-hidden>
                                                        <i className="bi bi-bag" />
                                                    </span>
                                                )}
                                            </div>
                                            <div className="order-success-item__body">
                                                <p className="order-success-item__title">{title}</p>
                                                <p className="order-success-item__meta">
                                                    Size {item.selectedSize} · Qty {item.selectedQty}
                                                </p>
                                            </div>
                                            <div className="order-success-item__price">
                                                ₹{formatCurrency(item.total_price ?? (item.unit_price || 0) * (item.selectedQty || 1))}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            {moreCount > 0 && (
                                <p className="order-success-more-items">+{moreCount} more item{moreCount > 1 ? 's' : ''}</p>
                            )}
                        </section>
                    )}

                    {address && (
                        <section className="order-success-card order-success-card--delivery">
                            <h2 className="order-success-card__heading">
                                <i className="bi bi-geo-alt" aria-hidden /> Delivery address
                            </h2>
                            <div className="order-success-address">
                                {address.name && <p className="order-success-address__name">{address.name}</p>}
                                {address.mobile && <p className="order-success-address__phone">{address.mobile}</p>}
                                <p className="order-success-address__lines">
                                    {[address.streetAddress, address.city, address.state, address.pincode]
                                        .filter(Boolean)
                                        .join(', ')}
                                </p>
                            </div>
                            <p className="order-success-delivery-note">
                                <i className="bi bi-truck" aria-hidden />
                                Estimated delivery in 5–7 business days
                            </p>
                        </section>
                    )}
                </div>

                <div className={`order-success-actions${mob ? ' order-success-actions--mob' : ''}`}>
                    <button type="button" className="order-success-btn order-success-btn--primary" onClick={handleViewOrders}>
                        View my orders
                    </button>
                    <button type="button" className="order-success-btn order-success-btn--secondary" onClick={handleContinueShopping}>
                        Continue shopping
                    </button>
                </div>
            </div>
        </div>
    );
}
